/**
 * RevenueDashboardView — Phase 4 Payments (G.20). Lloyd-only revenue snapshot.
 * Self-gates: the backend returns 403 unless the manager has "can view payments", in which case
 * this shows an access message instead of the numbers.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import PaymentApi, {
  RevenueSummaryDto,
  RevenueChannelDto,
  PaymentsForbiddenError,
} from '../../api/PaymentApi';
import { formatCentsUsd } from '../../constants/paymentPresets';
import PaymentSearchPanel from '../../components/PaymentSearchPanel/PaymentSearchPanel';

const Metric: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Paper sx={{ p: 3, minWidth: 0, overflow: 'hidden' }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, wordBreak: 'break-word', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
        {hint}
      </Typography>
    )}
  </Paper>
);

/**
 * One collection channel across the standard periods. Stripe and non-Stripe money are shown in
 * separate blocks rather than one figure: the Stripe number has to stay reconcilable against the
 * Stripe dashboard, and cash/Zelle/cheque money never appears there.
 */
const ChannelSection: React.FC<{
  title: string;
  caption?: string;
  channel: RevenueChannelDto;
  countLabel: string;
}> = ({ title, caption, channel, countLabel }) => (
  <Box component="section">
    <Typography variant="h6" sx={{ mb: caption ? 0.5 : 2 }}>
      {title}
    </Typography>
    {caption && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {caption}
      </Typography>
    )}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
        },
        gap: 2,
        alignContent: 'start',
      }}
    >
      <Metric label="Today" value={formatCentsUsd(channel.todayCents)} />
      <Metric label="This week" value={formatCentsUsd(channel.weekCents)} />
      <Metric label="This month" value={formatCentsUsd(channel.monthCents)} />
      <Metric label="This year" value={formatCentsUsd(channel.yearCents)} />
      <Metric
        label="All time"
        value={formatCentsUsd(channel.allTimeCents)}
        hint={`${channel.paidLinkCount} ${countLabel}`}
      />
      {/*
        Money collected for a court and owed onward. Excluded from every figure above — shown here
        rather than silently dropped, because a total that quietly shrinks is a total that gets
        queried, and because what the firm owes a court is a real obligation worth seeing.
      */}
      {channel.passedThroughToCourtsCents > 0 && (
        <Metric
          label="Passed through to courts"
          value={formatCentsUsd(channel.passedThroughToCourtsCents)}
          hint="Collected for a court — not firm revenue, and not counted above"
        />
      )}
    </Box>
  </Box>
);

const RevenueDashboardView: React.FC = () => {
  const [summary, setSummary] = useState<RevenueSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setSummary(await PaymentApi.getRevenueSummary());
      } catch (err) {
        if (err instanceof PaymentsForbiddenError) setForbidden(true);
        else setError(err instanceof Error ? err.message : 'Failed to load revenue');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Revenue
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : forbidden ? (
          <Alert severity="info">You don't have access to revenue metrics.</Alert>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : summary ? (
          <Stack spacing={5}>
            <PaymentSearchPanel />

            <ChannelSection
              title="Total collected"
              caption="Every payment received on a case, however it arrived."
              channel={summary.collectedTotal}
              countLabel="payment(s)"
            />

            <ChannelSection
              title="Collected via Stripe"
              channel={summary.collectedViaStripe}
              countLabel="paid link(s)"
            />

            <ChannelSection
              title="Collected outside Stripe"
              caption="Cash, Zelle, checks and payments already taken in Clio, as recorded by staff."
              channel={summary.collectedOutsideStripe}
              countLabel="recorded payment(s)"
            />

            <Box component="section">
              <Typography variant="h6" sx={{ mb: 2 }}>
                Amounts on record
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 2,
                  alignContent: 'start',
                }}
              >
                <Metric
                  label="Total on citations"
                  value={formatCentsUsd(summary.onRecord.citationAmountTotalCents)}
                  hint={`${summary.onRecord.citationCount} case(s) with an amount`}
                />
                <Metric
                  label="Pre-paid third-party"
                  value={formatCentsUsd(summary.onRecord.prepaidThirdPartyTotalCents)}
                  hint={`${summary.onRecord.prepaidThirdPartyCount} pre-paid case(s)`}
                />
              </Box>
            </Box>
          </Stack>
        ) : null}
      </Container>
    </>
  );
};

export default RevenueDashboardView;
